/**
 * UI-side types for the /for-advocates onboarding flow.
 *
 * The core AdvocateSearchCase / ResultGroup / AdvocateCourt types live in
 * @/lib/advocates/types (shared with the scraper router). This file layers
 * on the streaming-event protocol emitted by POST /api/advocate-search and
 * consumed by SearchProgress / ResultsGrouped.
 */

import type {
  AdvocateCourt,
  AdvocateSearchCase,
  ResultGroup,
} from '@/lib/advocates/types';

export type { AdvocateCourt, AdvocateSearchCase, ResultGroup };

export type CourtStatus = 'pending' | 'searching' | 'done' | 'error';

export type CourtReason = 'captcha' | 'upstream_unavailable' | 'timeout';

export interface CourtProgress {
  status: CourtStatus;
  count?: number;
  reason?: CourtReason;
}

export type CourtProgressMap = Record<AdvocateCourt, CourtProgress>;

/**
 * Events emitted by /api/advocate-search as newline-delimited JSON.
 * Keep this union in sync with the server route — both sides parse it.
 * The stream intentionally does NOT carry variants, timings, or raw
 * upstream error strings (per CLAUDE.md blog rules).
 */
export type SearchStreamEvent =
  | { type: 'started' }
  | { type: 'court_done'; court: AdvocateCourt; count: number; reason?: CourtReason }
  | {
      type: 'results';
      sessionId: string | null;
      groups: ResultGroup[];
      totalCases: number;
      activeCases: number;
    };

export const INITIAL_COURT_PROGRESS: CourtProgressMap = {
  district: { status: 'pending' },
  high_court: { status: 'pending' },
  supreme_court: { status: 'pending' },
  tribunal: { status: 'pending' },
};

/** Pretty-name per court used across UI copy — keeps phrasing out of scraper
 *  details (per CLAUDE.md blog rules: no scraper / infra language surfaces
 *  in user-facing strings). */
export const COURT_LABELS: Record<AdvocateCourt, string> = {
  district: 'District Courts',
  high_court: 'High Court',
  supreme_court: 'Supreme Court',
  tribunal: 'NCLT',
};
